import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config/serverApiConfig";
import { Avatar, Button, Descriptions, Form, Input, InputNumber, Modal, Select, Space, Spin, Tag, Typography, Upload, message, theme } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { getBase64 } from "../utils/history";
import { DEFAULT_AVATAR, ROLES, ROLE_LABELS, dummyRequest, initials } from "../Components/hrRoles";
import "../Components/matter/LabPreview.css";

const { Title } = Typography;

function HrDetail() {
  const { id } = useParams();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [hrForm] = Form.useForm();
  const [multipleDelete, setMultipleDelete] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [imageUrl, setImageUrl] = useState(DEFAULT_AVATAR);

  const handleUploadChange = (e) => {
    if (!e.file?.originFileObj) return;
    getBase64(e.file.originFileObj, (url64) => setImageUrl(url64));
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("تەنها فایلی JPG/PNG قبووڵە");
    }
    return isJpgOrPng;
  };

  const submitHr = (values) => {
    setConfirmLoading(true);
    const { image3, ...lawyer } = values;
    const fd = new FormData();

    if (image3?.fileList?.length) {
      fd.append("image", image3.fileList[0].originFileObj);
    }

    fd.append("user", JSON.stringify({ ...lawyer }));

    axios
      .put(`${API_BASE_URL}user/${id}`, fd)
      .then((res) => {
        setData(res.data);
        setOpen(false);
        message.success("کارمەند نوێکرایەوە");
      })
      .catch(() => {
        message.error("نەتوانرا کارمەند نوێ بکرێتەوە");
      })
      .finally(() => setConfirmLoading(false));
  };

  const deleteStaff = () => {
    setDeleteLoading(true);
    axios
      .delete(`${API_BASE_URL}user/${id}`)
      .then(() => {
        message.success("سڕایەوە 🎉");
        navigate(-1);
      })
      .catch(() => {
        message.error("نەتوانرا کارمەند بسڕدرێتەوە");
      })
      .finally(() => {
        setDeleteLoading(false);
        setMultipleDelete(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}user/${id}`)
      .then((res) => {
        setData(res.data || {});
        hrForm.setFieldsValue({ ...res.data });
        if (res.data?.image) setImageUrl(res.data.image);
      })
      .catch(() => {
        message.error("نەتوانرا کارمەند بهێنرێت");
      })
      .finally(() => setLoading(false));
  }, [id, hrForm]);

  return (
    <div
      className="lab-page"
      style={{
        "--lab-surface": token.colorBgContainer,
        "--note-accent": token.colorPrimary,
      }}
    >
      <div className="lab-page-head">
        <div>
          <Title level={3} style={{ color: token.colorPrimary }}>
            {data.name || "کارمەند"}
          </Title>
          <p>{ROLE_LABELS[data.role] || data.role || "زانیاری کارمەند"}</p>
        </div>
        <Space>
          <Button size="large" icon={<EditOutlined />} onClick={() => setOpen(true)}>
            چاککردن
          </Button>
          <Button danger size="large" icon={<DeleteOutlined />} onClick={() => setMultipleDelete(true)}>
            سڕینەوە
          </Button>
        </Space>
      </div>

      {loading && !data.id ? (
        <div className="cliche-empty">
          <Spin />
        </div>
      ) : (
        <div className="lab-form-card hr-detail-card">
          <Avatar size={120} src={data.image || undefined} style={{ backgroundColor: token.colorPrimary }}>
            {initials(data.name)}
          </Avatar>
          <Descriptions column={1} bordered className="hr-detail-info">
            <Descriptions.Item label="ناوی کارمەند">{data.name}</Descriptions.Item>
            <Descriptions.Item label="ناوی بەکارهێنەر">{data.username}</Descriptions.Item>
            <Descriptions.Item label="ژم. مۆبایل">{data.phoneNumber}</Descriptions.Item>
            <Descriptions.Item label="ڕەگەز">{data.gender}</Descriptions.Item>
            <Descriptions.Item label="ڕۆڵ">{ROLE_LABELS[data.role] || data.role}</Descriptions.Item>
            <Descriptions.Item label="تەمەن">{data.age}</Descriptions.Item>
            <Descriptions.Item label="وشەی نهێنی">{data.password}</Descriptions.Item>
            <Descriptions.Item label="بار">
              <Tag color={data.status === false ? "volcano" : "green"}>{data.status === false ? "ناچالاک" : "چالاک"}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}

      <Modal
        open={multipleDelete}
        onCancel={() => setMultipleDelete(false)}
        onOk={deleteStaff}
        title="ئایا دڵنیایت لە سڕینەوەی کارمەند؟"
        okText="بەڵێ"
        centered
        okType="danger"
        cancelText="نەخێر"
        confirmLoading={deleteLoading}
      />

      <Modal
        open={open}
        centered
        okText="چاککردن"
        cancelText="پاشگەزبوونەوە"
        confirmLoading={confirmLoading}
        onOk={() => hrForm.submit()}
        onCancel={() => setOpen(false)}
        title={
          <Title style={{ color: token.colorPrimary }} level={5}>
            چاکردنی کارمەند
          </Title>
        }
      >
        <Form name="AWS" form={hrForm} layout="vertical" onFinish={submitHr}>
          <Form.Item name="image3" className="hr-avatar-field">
            <Upload listType="picture" customRequest={dummyRequest} showUploadList={false} beforeUpload={beforeUpload} onChange={handleUploadChange}>
              <img src={imageUrl} alt="کارمەند" className="hr-avatar-upload" />
            </Upload>
          </Form.Item>

          <Form.Item label="ناوی کارمەند" name="name">
            <Input size="large" placeholder="ناوی کارمەند" />
          </Form.Item>

          <Form.Item label="ناوی بەکارهێنەر" name="username">
            <Input size="large" placeholder="ناوی بەکارهێنەر" />
          </Form.Item>

          <Form.Item label="ژم. مۆبایل" name="phoneNumber">
            <Input size="large" placeholder="ژم. مۆبایل" />
          </Form.Item>

          <Form.Item label="ڕەگەز" name="gender">
            <Select size="large" placeholder="ڕەگەز">
              <Select.Option value="نێر">نێر</Select.Option>
              <Select.Option value="مێ">مێ</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="ڕۆڵ" name="role">
            <Select size="large" placeholder="ڕۆڵ">
              {ROLES.filter((role) => role.id !== "all").map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="تەمەن" name="age">
            <InputNumber size="large" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="وشەی نهێنی" name="password" rules={[{ required: true, message: "وشەی نهێنی پێویستە" }]}>
            <Input.Password size="large" placeholder="وشەی نهێنی" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default HrDetail;
